import {
  useState,
  useCallback,
  useImperativeHandle,
  useEffect,
  useRef,
  forwardRef,
  createContext,
  useContext,
  type ReactNode,
} from "react";
import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  DateRangePicker,
  FilterSelect,
  FilterContext,
  type DateRange,
} from "@/components/designSystem";
import { formatDateToString } from "@/common/utils/date";
import { Check, Plus } from "@/components/icons";
import { Button } from "@/components/ui/button";
import type { FilterOption } from "@/components/designSystem/FilterSelect";
import type { ZodType } from "zod";

// ==========================================================================
// Context
// ==========================================================================
interface AdditionalFiltersContextValue {
  visibleFilters: string[];
  dateRanges: Record<string, DateRange>;
  setDateRange: (label: string, range: DateRange) => void;
}

const AdditionalFiltersContext =
  createContext<AdditionalFiltersContextValue | null>(null);

// ==========================================================================
// Handle (ref로 노출)
// ==========================================================================
export interface AdditionalFiltersHandle {
  reset: () => void;
}

// ==========================================================================
// 하위 컴포넌트: DateRange
// ==========================================================================
interface AdditionalDateRangeProps {
  label: string;
  startParamKey?: string;
  endParamKey?: string;
}

function AdditionalDateRange({
  label,
  startParamKey,
  endParamKey,
}: AdditionalDateRangeProps) {
  const ctx = useContext(AdditionalFiltersContext);
  const filterCtx = useContext(FilterContext);

  const isVisible = ctx?.visibleFilters.includes(label) ?? false;
  const restoredRef = useRef(false);

  // 복원 시 store 값으로 dateRanges 초기화
  useEffect(() => {
    if (restoredRef.current || !isVisible || !ctx || !filterCtx) return;
    restoredRef.current = true;

    const startVal = startParamKey ? filterCtx.filterValues[startParamKey] : null;
    const endVal = endParamKey ? filterCtx.filterValues[endParamKey] : null;
    if (startVal || endVal) {
      ctx.setDateRange(label, {
        startDate: startVal ? new Date(startVal as string) : null,
        endDate: endVal ? new Date(endVal as string) : null,
      });
    }
  }, [isVisible, ctx, filterCtx, label, startParamKey, endParamKey]);

  if (!ctx || !isVisible) return null;

  return (
    <DateRangePicker
      label={label}
      value={ctx.dateRanges[label] ?? { startDate: null, endDate: null }}
      onChange={(range) => {
        ctx.setDateRange(label, range);
        if (filterCtx && startParamKey && endParamKey) {
          filterCtx.setParams(
            startParamKey,
            formatDateToString(range.startDate),
          );
          filterCtx.setParams(
            endParamKey,
            formatDateToString(range.endDate),
          );
        }
      }}
    />
  );
}

// ==========================================================================
// 하위 컴포넌트: Select
// ==========================================================================
interface AdditionalSelectProps {
  label: string;
  paramKey: string;
  options: FilterOption[];
  schema?: ZodType;
  /** 옵션이 많은 필터(고객사 등)는 검색창을 붙인다 */
  showSearch?: boolean;
}

function AdditionalSelect({
  label,
  paramKey,
  options,
  schema,
  showSearch,
}: AdditionalSelectProps) {
  const ctx = useContext(AdditionalFiltersContext);
  if (!ctx || !ctx.visibleFilters.includes(label)) return null;

  return (
    <FilterSelect
      label={label}
      paramKey={paramKey}
      options={options}
      schema={schema}
      showSearch={showSearch}
    />
  );
}

// ==========================================================================
// 메인 컴포넌트: AdditionalFilters
// ==========================================================================
interface AdditionalFiltersProps {
  children: ReactNode;
}

export const AdditionalFilters = Object.assign(
  forwardRef<AdditionalFiltersHandle, AdditionalFiltersProps>(
    function AdditionalFilters({ children }, ref) {
      // children에서 label 및 paramKey 추출
      const filterLabels: string[] = [];
      const filterParamKeys: { label: string; keys: string[] }[] = [];
      React.Children.forEach(children, (child) => {
        if (React.isValidElement<{ label: string; paramKey?: string; startParamKey?: string; endParamKey?: string }>(child) && child.props.label) {
          filterLabels.push(child.props.label);
          const keys: string[] = [];
          if (child.props.paramKey) keys.push(child.props.paramKey);
          if (child.props.startParamKey) keys.push(child.props.startParamKey);
          if (child.props.endParamKey) keys.push(child.props.endParamKey);
          filterParamKeys.push({ label: child.props.label, keys });
        }
      });

      const filterCtx = useContext(FilterContext);

      // 세션에 값이 남아 있는 추가 필터는 처음부터 펼쳐 둔다.
      // 첫 렌더에서 정하므로, effect 로 켜던 때처럼 값이 있는 필터가 한 프레임 사라져
      // 보이는 일이 없다
      const [visibleFilters, setVisibleFilters] = useState<string[]>(() =>
        filterParamKeys
          .filter(({ keys }) =>
            keys.some((key) => {
              const value = filterCtx?.filterValues[key];
              return value !== null && value !== undefined && value !== "";
            }),
          )
          .map(({ label }) => label),
      );
      const [dateRanges, setDateRanges] = useState<Record<string, DateRange>>(
        {},
      );
      const [popoverOpen, setPopoverOpen] = useState(false);

      useImperativeHandle(ref, () => ({
        reset() {
          setVisibleFilters([]);
          setDateRanges({});
        },
      }));

      const setDateRange = useCallback(
        (label: string, range: DateRange) => {
          setDateRanges((prev) => ({ ...prev, [label]: range }));
        },
        [],
      );

      function handleToggleFilter(label: string) {
        const isVisible = visibleFilters.includes(label);
        if (!isVisible) {
          setVisibleFilters((prev) => [...prev, label]);
          return;
        }

        // 토글 해제 시 해당 필터가 쓰던 store 값도 함께 비운다
        const keys =
          filterParamKeys.find((entry) => entry.label === label)?.keys ?? [];
        for (const key of keys) filterCtx?.setParams(key, null);

        setVisibleFilters((prev) => prev.filter((v) => v !== label));
        setDateRanges((ranges) => {
          const next = { ...ranges };
          delete next[label];
          return next;
        });
      }

      return (
        <AdditionalFiltersContext.Provider
          value={{ visibleFilters, dateRanges, setDateRange }}
        >
          {children}

          {/* 필터 추가 버튼 */}
          <PopoverPrimitive.Root
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          >
            <PopoverPrimitive.Trigger asChild>
              <Button theme="normal" variant="outline" size="sm">
                <Plus size={16} />
                필터 추가
              </Button>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                className="z-50 w-60 rounded-xl bg-static-white shadow-[0px_8px_20px_rgba(0,0,0,0.08)] inset-border-filter-normal overflow-hidden"
                sideOffset={4}
                align="start"
              >
                <div className="flex flex-col py-3">
                  {filterLabels.map((label) => {
                    const isSelected = visibleFilters.includes(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-left hover:bg-fills-light transition-colors"
                        onClick={() => handleToggleFilter(label)}
                      >
                        <span
                          className={`flex items-center justify-center size-5 rounded border ${
                            isSelected
                              ? "bg-primary-normal border-primary-normal"
                              : "border-lines-normal bg-static-white"
                          }`}
                        >
                          {isSelected && (
                            <Check size={14} color="static-white" />
                          )}
                        </span>
                        <span className="text-sm font-medium text-labels-primary">
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </AdditionalFiltersContext.Provider>
      );
    },
  ),
  {
    DateRange: AdditionalDateRange,
    Select: AdditionalSelect,
  },
);
